import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import styles from './SMLPublishedThumb.module.scss';
import { usePreviewPageLang } from '../smlPreview/usePreviewPageLang';

interface SMLPublishedThumbProps {
  /** The published page itself, shown in a frame at a fraction of its size. */
  previewUrl: string | null;
  /** What to show while there is no page to frame: the artwork the page carries. */
  coverUrl: string | null;
}

/**
 * The page, small.
 *
 * The old modal filled this space with stock artwork of a rocket. What belongs
 * here is the page that just went live, in its own theme, so the artist can see
 * that the thing they published is the thing they meant to publish. The frame
 * is inert — it is a picture, not a second editor.
 */
const SMLPublishedThumbComponent: React.FC<SMLPublishedThumbProps> = ({ previewUrl, coverUrl }) => {
  const intl = useIntl();
  const withLang = usePreviewPageLang();
  const frameUrl = useMemo(() => (previewUrl === null ? null : withLang(previewUrl)), [previewUrl, withLang]);

  if (frameUrl === null && coverUrl === null) {
    return null;
  }

  return (
    <div className={styles.frame}>
      {frameUrl !== null ? (
        <iframe
          className={styles.page}
          src={frameUrl}
          title={intl.formatMessage({ id: 'app.sml.published.thumb', defaultMessage: 'Your page' })}
          tabIndex={-1}
        />
      ) : (
        <img className={styles.cover} src={coverUrl ?? ''} alt="" />
      )}
    </div>
  );
};

export const SMLPublishedThumb = TypedMemo(SMLPublishedThumbComponent);
